/**
 * The toast the product reserved `--z-toast` for and never built (WS-Z7).
 *
 * Z0 deleted `.toast-pill` — a toast style with no component — and left the z-index reserved
 * with a note saying "for the day one arrives". This is that day. `docs/design.md` already
 * names the channel twice: rule 9 says an informational banner is the caption voice and
 * "this worked" is a badge or a TOAST, and the button table says success is a state, never
 * an affordance. There was no way to say it.
 *
 * What it is for, and nothing else: CONFIRMING an action that already happened. A failure is
 * a `.banner--danger` where the failure is, because a failure needs to stay on screen and
 * usually needs a control beside it; a toast leaves. Nothing is ever ONLY in a toast.
 *
 * Deliberately not a store: the host subscribes to one module-level set. A confirmation that
 * a workspace has finished with in four seconds does not belong in application state, and
 * keeping it here means no feature has to import zustand to say "done".
 */
import { useEffect, useRef, useState } from "react";
import "./ui.css";

export interface ToastEntry {
  id: number;
  message: string;
}

/** How long a confirmation stays. Long enough to read one sentence, short enough to ignore. */
export const TOAST_MS = 4000;

let nextId = 1;
let entries: ToastEntry[] = [];
const listeners = new Set<(e: ToastEntry[]) => void>();

function publish() {
  const snapshot = entries;
  for (const l of listeners) l(snapshot);
}

/** Confirm something that HAS happened. Never used to report a failure — see the header. */
export function toast(message: string): void {
  const entry = { id: nextId++, message };
  entries = [...entries, entry];
  publish();
}

export function dismissToast(id: number): void {
  entries = entries.filter((e) => e.id !== id);
  publish();
}

/** Test seam: drop every pending confirmation, so one suite cannot leak into the next. */
export function clearToasts(): void {
  entries = [];
  publish();
}

/**
 * The one mount site, in the shell. `aria-live="polite"` and NOT `role="alert"`: a
 * confirmation must not interrupt what a screen-reader user is in the middle of reading.
 */
export function ToastHost() {
  const [items, setItems] = useState<ToastEntry[]>(entries);
  // Timers keyed by entry id, so a re-render never restarts a countdown already running.
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    listeners.add(setItems);
    setItems(entries);
    return () => { listeners.delete(setItems); };
  }, []);

  useEffect(() => {
    for (const item of items) {
      if (timers.current.has(item.id)) continue;
      timers.current.set(item.id, setTimeout(() => {
        timers.current.delete(item.id);
        dismissToast(item.id);
      }, TOAST_MS));
    }
    for (const [id, t] of timers.current) {
      if (!items.some((i) => i.id === id)) { clearTimeout(t); timers.current.delete(id); }
    }
  }, [items]);

  useEffect(() => {
    const running = timers.current;
    return () => { for (const t of running.values()) clearTimeout(t); running.clear(); };
  }, []);

  if (items.length === 0) return null;
  return (
    <div className="toasts" role="status" aria-live="polite" data-testid="toasts">
      {items.map((t) => (
        <div key={t.id} className="toast" data-testid="toast">
          <span className="toast__msg">{t.message}</span>
          <button
            type="button"
            className="btn btn--sm btn--ghost toast__close"
            aria-label="Dismiss"
            data-testid="toast-dismiss"
            onClick={() => dismissToast(t.id)}
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}
